import { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { doc, writeBatch } from 'firebase/firestore';
import { Check, Info, Users } from 'lucide-react';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { saveLocalFirst } from '../lib/save-local-first';
import { tapLight, tapMedium } from '../lib/haptics';
import { useIsWide } from '../hooks/useIsWide';
import { DefenseGrid } from './plan/DefenseGrid';
import { WizardBar, WizardHead, WizardFoot, WizardList, WizardRow } from './plan/WizardChrome';
import {
  addPlayerToGame, benchLogUpdates, loadGamePositions,
  removePlayerFromGame, swapIntoPosition, toInningLogs, type InningEntry,
} from '../lib/game-positions';
import { fieldTimeRecordFor, fieldTimeSourceOf, gameLengthInnings } from '../lib/gc-innings';

// Edit Game → Edit Positions for a FINAL game. Fixes the record of who played
// where, cell by cell, and nothing else. Deliberately not the Game Planner: the
// planner can rebuild every inning (fill mode, Auto-generate, Use last game),
// which on a finished game silently replaced what really happened (support log
// G2). Scheduled games still open the planner.
//
// Save writes the game's `inningLogs` (never `plan`) and, when those positions
// are the game's field-time record under the team's "Field positions come from"
// setting, each player's bench count for it, so Field Time Fairness sees the
// corrected game.

const MIN_INNINGS = 1;
const MAX_INNINGS = 12;

export const GamePositionsEditor = ({ game, team, players, gameDayPlayers, onClose }: {
  game: any;
  team: any;
  /** Every player doc on the team: names for the grid, benchLog for the save. */
  players: any[];
  /** Who could have played (active roster plus active guests), for Who played. */
  gameDayPlayers: any[];
  onClose: () => void;
}) => {
  const isWide = useIsWide();
  const initial = useMemo(
    () => loadGamePositions(game, gameDayPlayers.map((p: any) => p.id), team?.defaultInnings || 6),
    // Loaded once per opening; later snapshots must not reset the coach's edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [innings, setInnings] = useState<InningEntry[][]>(initial.innings);
  const [numInnings, setNumInnings] = useState(initial.numInnings);
  const [rowIds, setRowIds] = useState<string[]>(initial.rowIds);
  const [whoPlayedOpen, setWhoPlayedOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const source = fieldTimeSourceOf(team);
  // GameChanger's field time is this game's record right now. On a GameChanger
  // team, edits here won't reach the Stats tab. On a Dugout team, saving every
  // inning of the game here makes these positions the record instead.
  const gcIsRecordNow = fieldTimeRecordFor(game, source, team?.defaultInnings) === 'gc';
  const gameInnings = Math.ceil(gameLengthInnings(game, team?.defaultInnings) - 0.01);
  const byId = (id: string) => players.find((p: any) => p.id === id) ?? { id, name: 'Former player' };
  const rows = rowIds.map(byId);

  const subtitle = initial.source === 'plan'
    ? 'Starting from your plan. Change what was different.'
    : initial.source === 'blank'
      ? 'Everyone starts on the bench. Tap a position to change it.'
      : 'How this game was played. Tap a position to fix it.';

  const handleSetPosition = (inningIdx: number, playerId: string, position: string) => {
    tapLight();
    setInnings(prev => {
      const next = [...prev];
      next[inningIdx] = swapIntoPosition(next[inningIdx] ?? [], position, playerId);
      return next;
    });
  };

  const handleCopyToLaterInnings = (inningIdx: number) => {
    const source = innings[inningIdx];
    if (!source?.length) return;
    tapMedium();
    setInnings(prev => {
      const next = [...prev];
      for (let i = inningIdx + 1; i < numInnings; i++) next[i] = source.map(e => ({ ...e }));
      return next;
    });
  };

  // An added inning starts as a copy of the last one, since that is usually
  // how the next inning was played. The coach changes what was different.
  const handleAddInning = () => {
    if (numInnings >= MAX_INNINGS) return;
    tapLight();
    setInnings(prev => {
      const next = prev.slice(0, numInnings);
      next[numInnings] = (next[numInnings - 1] ?? []).map(e => ({ ...e }));
      return next;
    });
    setNumInnings(n => n + 1);
  };

  const handleRemoveInning = () => {
    if (numInnings <= MIN_INNINGS) return;
    tapLight();
    setInnings(prev => prev.slice(0, numInnings - 1));
    setNumInnings(n => n - 1);
  };

  const togglePlayed = (playerId: string) => {
    tapLight();
    if (rowIds.includes(playerId)) {
      setRowIds(ids => ids.filter(id => id !== playerId));
      setInnings(prev => removePlayerFromGame(prev, playerId));
    } else {
      setRowIds(ids => [...ids, playerId]);
      setInnings(prev => addPlayerToGame(prev, playerId));
    }
  };

  const handleSave = async () => {
    if (saving) return;
    tapMedium();
    setSaving(true);
    const now = new Date().toISOString();
    const inningLogs = toInningLogs(innings, numInnings);
    try {
      const batch = writeBatch(db);
      // update() replaces the whole map, so a removed inning is removed too.
      batch.update(doc(db, `teams/${team.id}/games`, game.id), { inningLogs, updatedAt: now });
      // Bench counts follow whichever record describes the game AFTER this
      // save. If GameChanger's field time still does, the import owns them.
      if (fieldTimeRecordFor({ ...game, inningLogs }, source, team?.defaultInnings) === 'live') {
        for (const u of benchLogUpdates(players, game.id, inningLogs)) {
          batch.update(doc(db, `teams/${team.id}/players`, u.id), {
            benchLog: u.benchLog,
            seasonBenchInnings: u.seasonBenchInnings,
            updatedAt: now,
          });
        }
      }
      await saveLocalFirst(batch.commit(), 'Edit positions');
      onClose();
    } catch (error) {
      setSaving(false);
      handleFirestoreError(error, OperationType.UPDATE, `teams/${team.id}/games/${game.id}`);
    }
  };

  // Who played: the roster plus anyone already in this game's record (a player
  // since retired still played in it).
  const candidates = [
    ...gameDayPlayers,
    ...rowIds.filter(id => !gameDayPlayers.some((p: any) => p.id === id)).map(byId),
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="fixed inset-0 z-[100] flex flex-col safe-top safe-x bg-canvas"
    >
      <div className="relative flex-1 min-h-0 flex flex-col">
        <WizardBar
          label={`vs ${game.opponent || 'Opponent'}`}
          onClose={onClose}
          right={
            <button onClick={() => setWhoPlayedOpen(true)} aria-label="Who played" className="p-1 -mr-1 text-ink-2">
              <Users className="w-5 h-5" />
            </button>
          }
        />
        <WizardHead title="Game Positions" sub={subtitle} />

        <div className="flex-1 overflow-y-auto bg-canvas px-4 pt-4">
          {gcIsRecordNow && (
            <div className="flex items-start gap-2.5 bg-raised rounded-card px-4 py-3 mb-4">
              <Info className="w-4 h-4 text-ink-2 shrink-0 mt-0.5" />
              <p className="text-[13.5px] font-medium text-ink-2 leading-snug">
                {source !== 'dugout'
                  ? "This game's field time comes from your GameChanger import, so changes here won't show on the Stats tab."
                  : numInnings < gameInnings
                    ? `This game's field time comes from your GameChanger import, which has ${gameInnings} innings. Add the missing innings and save to use your positions instead.`
                    : "This game's field time comes from your GameChanger import. Your team uses Dugout positions, so saving here replaces it."}
              </p>
            </div>
          )}

          <button
            onClick={() => setWhoPlayedOpen(true)}
            className="w-full flex items-center justify-between px-1 mb-3 text-left"
          >
            <span className="text-sm font-semibold text-ink-2">Who played</span>
            <span className="text-[13px] font-semibold text-ink tabular-nums">{rowIds.length} players · Change</span>
          </button>

          {rowIds.length > 0 ? (
            <DefenseGrid
              innings={innings}
              numInnings={numInnings}
              players={rows}
              settings={team.settings}
              onSetPosition={handleSetPosition}
              onCopyToLaterInnings={handleCopyToLaterInnings}
              onAddInning={numInnings < MAX_INNINGS ? handleAddInning : undefined}
              onRemoveInning={numInnings > MIN_INNINGS ? handleRemoveInning : undefined}
              wide={isWide}
            />
          ) : (
            <p className="text-sm text-ink-2 px-1 py-6 text-center">
              No players in this game. Tap Who played to add them.
            </p>
          )}

          <div className="h-4" />
        </div>

        <WizardFoot
          primaryLabel={saving ? 'Saving...' : 'Save positions'}
          onPrimary={handleSave}
          secondaryLabel="Cancel"
          onSecondary={onClose}
        />

        {whoPlayedOpen && (
          <div className="absolute inset-0 z-10 flex flex-col bg-canvas">
            <WizardBar label="Who played" onBack={() => setWhoPlayedOpen(false)} />
            <WizardHead
              title="Who played?"
              sub="Players you take out are removed from every inning of this game."
            />
            <div className="flex-1 overflow-y-auto px-4 pt-2">
              <WizardList>
                {candidates.map((p: any, i: number) => {
                  const played = rowIds.includes(p.id);
                  return (
                    <WizardRow
                      key={p.id}
                      title={p.name}
                      meta={p.number ? `#${p.number}` : undefined}
                      onClick={() => togglePlayed(p.id)}
                      dim={!played}
                      last={i === candidates.length - 1}
                      trailing={
                        <span className={`w-6 h-6 rounded-full shrink-0 flex items-center justify-center ${played ? 'bg-ink' : 'border border-hairline'}`}>
                          {played && <Check className="w-4 h-4 text-surface" />}
                        </span>
                      }
                    />
                  );
                })}
              </WizardList>
              <div className="h-4" />
            </div>
            <WizardFoot primaryLabel="Done" onPrimary={() => setWhoPlayedOpen(false)} />
          </div>
        )}
      </div>
    </motion.div>
  );
};
